import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Stack, TextField, Typography,
} from '@mui/material';
import { AccountBalanceWalletRounded, CreditCardRounded, PaymentsRounded, ReceiptLongRounded } from '@mui/icons-material';
import { formatMoney } from '../utils/format';

function normalizeKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function boolish(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if ([1, '1', 'true', 'TRUE'].includes(value)) return true;
  if ([0, '0', 'false', 'FALSE'].includes(value)) return false;
  return fallback;
}

function validInt(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function normalizeMethod(row, index) {
  const relationId = validInt(row?.payment_relation_id ?? row?.relationId ?? row?.relation_id ?? row?.relacion_id ?? row?.configuracion_metodo_condicion_id);
  const methodId = validInt(row?.payment_method_id ?? row?.methodId ?? row?.method_id ?? row?.metodopago_id ?? row?.metodo_pago_id);
  const conditionId = validInt(row?.payment_condition_id ?? row?.conditionId ?? row?.condition_id ?? row?.condicionpago_id ?? row?.condicion_pago_id);
  const key = normalizeKey(row?.payment_method_key ?? row?.key ?? row?.metodopago_clave ?? row?.metodo_pago_clave ?? row?.clave ?? row?.nombre);
  if (!key) return null;
  return {
    ...row,
    uiId: String(relationId || methodId || row?.id || index + 1),
    relationId,
    methodId,
    conditionId,
    key,
    conditionKey: normalizeKey(row?.payment_condition_key ?? row?.conditionKey ?? row?.condicionpago_clave ?? row?.condicion_pago_clave),
    label: row?.payment_method_name ?? row?.label ?? row?.metodopago_nombre ?? row?.metodo_pago_nombre ?? row?.nombre ?? key,
    requiereReferencia: boolish(row?.requiereReferencia ?? row?.requiere_referencia, false),
    permiteVuelto: boolish(row?.permiteVuelto ?? row?.permite_vuelto, key === 'EFECTIVO'),
    visible: row?.visible_pos === undefined ? true : boolish(row.visible_pos, true),
    activo: row?.activo === undefined ? true : boolish(row.activo, true),
  };
}

function methodIcon(method) {
  if (method?.key === 'EFECTIVO') return <AccountBalanceWalletRounded />;
  if (/TARJETA|TRANSBANK|DEBITO|CREDITO/.test(method?.key || '')) return <CreditCardRounded />;
  if (method?.requiereReferencia) return <ReceiptLongRounded />;
  return <PaymentsRounded />;
}

function roundCash(value) {
  const amount = Math.round(Number(value || 0));
  const sign = amount < 0 ? -1 : 1;
  const abs = Math.abs(amount);
  const remainder = abs % 10;
  if (remainder === 0) return amount;
  return sign * (remainder <= 5 ? abs - remainder : abs + (10 - remainder));
}

export default function PaymentModal({ open, total, methods = [], onClose, onConfirm, processing = false }) {
  const normalized = useMemo(
    () => methods.map(normalizeMethod).filter((row) => row && row.visible && row.activo),
    [methods]
  );
  const [selectedId, setSelectedId] = useState('');
  const [amount, setAmount] = useState('');
  const [received, setReceived] = useState('');
  const [reference, setReference] = useState('');
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState('');

  const applied = payments.reduce((sum, row) => sum + Number(row.amount_applied || 0), 0);
  const remaining = Math.max(0, Math.round(Number(total || 0) - applied));
  const selected = normalized.find((row) => row.uiId === selectedId) || normalized[0] || null;
  const amountValue = Math.max(0, Math.round(Number(amount || remaining || 0)));
  const receivedValue = Math.max(0, Math.round(Number(received || amountValue || 0)));
  const cash = selected?.key === 'EFECTIVO' || selected?.permiteVuelto;

  useEffect(() => {
    if (!open) return;
    setPayments([]);
    setSelectedId(normalized[0]?.uiId || '');
    setAmount(String(Math.round(Number(total || 0))));
    setReceived(String(Math.round(Number(total || 0))));
    setReference('');
    setError('');
  }, [open, total, normalized]);

  useEffect(() => {
    if (!open || !selected) return;
    setAmount(String(remaining));
    setReceived(String(remaining));
    setReference('');
  }, [open, remaining, selectedId, selected]);

  const addLeg = async () => {
    if (!selected || processing) return;
    setError('');
    const appliedAmount = Math.min(remaining, amountValue || remaining);
    if (appliedAmount <= 0) return setError('Ingrese un monto mayor a $0.');
    if (selected.requiereReferencia && !reference.trim()) return setError('Este medio de pago requiere referencia o voucher.');

    let amountReceived = appliedAmount;
    let change = 0;
    let cashRounding = 0;

    if (cash) {
      const roundedApplied = roundCash(appliedAmount);
      amountReceived = Math.max(roundedApplied, receivedValue || roundedApplied);
      change = Math.max(0, amountReceived - roundedApplied);
      cashRounding = roundedApplied - appliedAmount;
    }

    const leg = {
      relationId: selected.relationId,
      methodId: selected.methodId,
      conditionId: selected.conditionId,
      key: selected.key,
      conditionKey: selected.conditionKey,
      label: selected.label,
      amount_applied: appliedAmount,
      amount_paid: cash ? amountReceived - change : appliedAmount,
      amount_received: amountReceived,
      change_given: change,
      cash_rounding: cashRounding,
      reference: reference.trim() || null,
    };

    const next = [...payments, leg];
    const nextApplied = next.reduce((sum, row) => sum + Number(row.amount_applied || 0), 0);
    const nextRemaining = Math.max(0, Math.round(Number(total || 0) - nextApplied));
    setPayments(next);

    if (nextRemaining <= 0) {
      try {
        await onConfirm?.(next);
      } catch (submitError) {
        setError(submitError?.message || 'No se pudo registrar la venta.');
      }
      return;
    }

    setAmount(String(nextRemaining));
    setReceived(String(nextRemaining));
    setReference('');
  };

  return (
    <Dialog open={open} onClose={processing ? undefined : onClose} fullWidth maxWidth="md" disableEscapeKeyDown={processing}>
      <DialogTitle>
        <Typography sx={{ fontSize: 22, fontWeight: 950 }}>Cobrar {formatMoney(total)}</Typography>
        <Typography variant="body2" color="text.secondary">Puede combinar varios medios de pago.</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert> : null}
        {payments.length ? (
          <Box sx={{ mb: 1.2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={900}>PAGOS APLICADOS</Typography>
            {payments.map((row, index) => (
              <Stack key={`${row.key}-${index}`} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                <Typography variant="body2">{row.label}</Typography>
                <Typography variant="body2" fontWeight={900}>{formatMoney(row.amount_applied)}</Typography>
              </Stack>
            ))}
            <Divider sx={{ mt: 0.5 }} />
          </Box>
        ) : null}

        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.2 }}>
          <Typography fontWeight={900}>Saldo</Typography>
          <Typography sx={{ fontSize: 29, color: '#0874ef', fontWeight: 1000 }}>{formatMoney(remaining)}</Typography>
        </Stack>

        <Grid container spacing={1} sx={{ mb: 1.4 }}>
          {normalized.map((method) => (
            <Grid item xs={6} sm={4} md={3} key={method.uiId}>
              <Button fullWidth variant={selected?.uiId === method.uiId ? 'contained' : 'outlined'} startIcon={methodIcon(method)} onClick={() => setSelectedId(method.uiId)} disabled={processing} sx={{ minHeight: 50 }}>
                {method.label}
              </Button>
            </Grid>
          ))}
        </Grid>

        {selected ? (
          <Stack spacing={1.1}>
            <TextField label="Monto a aplicar" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))} disabled={processing} />
            {cash ? <TextField label="Efectivo recibido" value={received} onChange={(e) => setReceived(e.target.value.replace(/\D/g, ''))} disabled={processing} /> : null}
            {selected.requiereReferencia ? <TextField label="Referencia / voucher" value={reference} onChange={(e) => setReference(e.target.value)} disabled={processing} /> : null}
            {cash && amountValue > 0 ? (
              <Alert severity="info">Vuelto estimado: {formatMoney(Math.max(0, receivedValue - roundCash(Math.min(remaining, amountValue))))}</Alert>
            ) : null}
          </Stack>
        ) : <Alert severity="warning">No hay medios de pago configurados y visibles en POS.</Alert>}
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={processing}>Cancelar</Button>
        <Button variant="contained" onClick={addLeg} disabled={!selected || remaining <= 0 || processing}>
          {processing ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Registrando venta…</> : remaining <= amountValue ? 'Confirmar pago' : 'Aplicar y continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
